// React Imports
import React, { useCallback, useMemo, type ReactNode } from 'react';

// MUI Imports
import type { ChipProps } from '@mui/material/Chip';
import Chip from '@mui/material/Chip';

import type {
    VerticalMenuDataType,
    VerticalSectionDataType,
    VerticalSubMenuDataType,
} from '@/types/menu.type';

// Component Imports
import { useAppSelector } from '@/redux-store/hooks';
import { permissionManager } from '@/utils/permission';

import {
    MenuSection,
    MenuItem as VerticalMenuItem,
    SubMenu as VerticalSubMenu,
} from '@menu/vertical-menu';
import { AppIcon } from './AppIcon';

// Generate a menu from the menu data array
export const GenerateVerticalMenu = React.memo(function ({
    menuData,
}: {
    menuData: VerticalMenuDataType[];
}) {
    // Hooks
    const user = useAppSelector(state => state.auth.user);

    const userPermissions = useMemo(() => user?.permissions ?? [], [user]);

    const renderMenuItems = useCallback(
        (data: VerticalMenuDataType[]) => {
            // Use the map method to iterate through the array of menu data
            return data.map((item: any) => {
                if (!item) {
                    //continue
                    return null;
                }

                const menuSectionItem = item as VerticalSectionDataType;
                const subMenuItem = item as VerticalSubMenuDataType;
                const menuItem = item;

                if (
                    !permissionManager.hasPermission(
                        menuItem.permissions,
                        userPermissions
                    )
                ) {
                    return null;
                }

                // Check if the current item is a section
                if (menuSectionItem.isSection) {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const { children, isSection, ...rest } = menuSectionItem;

                    // If it is, return a MenuSection component and call generateMenu with the current menuSectionItem's children
                    return (
                        <MenuSection key={item.id} {...rest}>
                            {children && renderMenuItems(children)}
                        </MenuSection>
                    );
                }

                // Check if the current item is a sub menu
                if (subMenuItem.children) {
                    const { children, icon, prefix, suffix, path, ...rest } =
                        subMenuItem;

                    const Icon = icon ? (
                        <AppIcon name={icon} size={20} />
                    ) : null;

                    const subMenuPrefix: ReactNode =
                        prefix && (prefix as ChipProps).label ? (
                            <Chip size='small' {...(prefix as ChipProps)} />
                        ) : (
                            (prefix as ReactNode)
                        );

                    const subMenuSuffix: ReactNode =
                        suffix && (suffix as ChipProps).label ? (
                            <Chip size='small' {...(suffix as ChipProps)} />
                        ) : (
                            (suffix as ReactNode)
                        );

                    // If it is, return a SubMenu component and call generateMenu with the current subMenuItem's children
                    return (
                        <VerticalSubMenu
                            key={item.id}
                            prefix={subMenuPrefix}
                            suffix={subMenuSuffix}
                            {...rest}
                            icon={Icon}
                        >
                            {children && renderMenuItems(children)}
                        </VerticalSubMenu>
                    );
                }

                // If the current item is neither a section nor a sub menu, return a MenuItem component
                const { label, icon, prefix, suffix, path, ...rest } = menuItem;

                // Localize the href
                const href =
                    typeof item.path === 'function' ? item.path() : rest.path;

                const Icon = icon ? <AppIcon name={icon} size={24} /> : null;

                const menuItemPrefix: ReactNode =
                    prefix && (prefix as ChipProps).label ? (
                        <Chip size='small' {...(prefix as ChipProps)} />
                    ) : (
                        (prefix as ReactNode)
                    );

                const menuItemSuffix: ReactNode =
                    suffix && (suffix as ChipProps).label ? (
                        <Chip size='small' {...(suffix as ChipProps)} />
                    ) : (
                        (suffix as ReactNode)
                    );

                return (
                    <VerticalMenuItem
                        key={item.id}
                        prefix={menuItemPrefix}
                        suffix={menuItemSuffix}
                        {...rest}
                        href={href}
                        icon={Icon}
                    >
                        {label}
                    </VerticalMenuItem>
                );
            });
        },
        [userPermissions]
    );

    const items = useMemo(
        () => renderMenuItems(menuData),
        [menuData, renderMenuItems]
    );

    return <>{items}</>;
});
